import { watch, type FSWatcher } from 'chokidar';
import { normalizeGlobPatterns } from '../utils/ignore-patterns.js';
import { validateStoreModelCompatibility } from '../utils/model-validation.js';
import type { IndexService } from './index.service.js';
import type { EmbeddingEngine } from '../db/embeddings.js';
import type { LanceStore } from '../db/lance.js';
import type { FileStore, RepoStore } from '../types/store.js';

export interface WatchServiceOptions {
  ignorePatterns?: readonly string[];
  /** Current embedding model ID for compatibility validation */
  currentModelId?: string;
}

export class WatchService {
  private readonly watchers: Map<string, FSWatcher> = new Map();
  private readonly pendingTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private readonly indexService: IndexService;
  private readonly lanceStore: LanceStore;
  private readonly embeddings: EmbeddingEngine;
  private readonly ignorePatterns: readonly string[];
  private readonly currentModelId: string | undefined;

  constructor(
    indexService: IndexService,
    lanceStore: LanceStore,
    embeddings: EmbeddingEngine,
    options: WatchServiceOptions = {}
  ) {
    this.indexService = indexService;
    this.lanceStore = lanceStore;
    this.embeddings = embeddings;
    // Use shared utility to normalize patterns to glob format with defaults
    this.ignorePatterns = normalizeGlobPatterns(options.ignorePatterns ?? []);
    this.currentModelId = options.currentModelId;
  }

  async watch(
    store: FileStore | RepoStore,
    debounceMs: number,
    onReindex: (() => void) | undefined,
    onError: (error: Error) => void
  ): Promise<void> {
    if (this.watchers.has(store.id)) {
      return Promise.resolve(); // Already watching
    }

    let timeout: NodeJS.Timeout | null = null;

    const watcher = watch(store.path, {
      ignored: [...this.ignorePatterns],
      persistent: true,
      ignoreInitial: true,
    });

    const reindexHandler = (): void => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        this.pendingTimeouts.delete(store.id);
        void (async (): Promise<void> => {
          try {
            // Validate model compatibility before incremental indexing
            // If currentModelId is set, check that store was indexed with same model
            if (this.currentModelId !== undefined) {
              validateStoreModelCompatibility(store, { currentModelId: this.currentModelId });
            }

            this.lanceStore.setDimensions(await this.embeddings.ensureDimensions());
            await this.lanceStore.initialize(store.id);

            // Try incremental indexing first if available, fall back to full indexing
            let useFullReindex = true;
            if (typeof this.indexService.indexStoreIncremental === 'function') {
              const incrementalResult = await this.indexService.indexStoreIncremental(store);
              if (incrementalResult.success) {
                useFullReindex = false;
              }
            }

            if (useFullReindex) {
              const fullResult = await this.indexService.indexStore(store);
              if (!fullResult.success) {
                onError(fullResult.error);
                return;
              }
            }

            onReindex?.();
          } catch (e) {
            const error = e instanceof Error ? e : new Error(String(e));
            onError(error);
          }
        })();
      }, debounceMs);
      this.pendingTimeouts.set(store.id, timeout);
    };

    watcher.on('all', reindexHandler);

    watcher.on('error', (e) => {
      const error = e instanceof Error ? e : new Error(String(e));
      onError(error);
    });

    this.watchers.set(store.id, watcher);
    return Promise.resolve();
  }

  async unwatch(storeId: string): Promise<void> {
    // Clear any pending timeout to prevent timer leak
    const pendingTimeout = this.pendingTimeouts.get(storeId);
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
      this.pendingTimeouts.delete(storeId);
    }

    const watcher = this.watchers.get(storeId);
    if (watcher) {
      await watcher.close();
      this.watchers.delete(storeId);
    }
  }

  async unwatchAll(): Promise<void> {
    for (const [id] of this.watchers) {
      await this.unwatch(id);
    }
  }
}
